import React, { useState, useEffect } from 'react';
import { Form, Button, Spinner, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useCreateRentMutation } from '../../slices/rentsApiSlice';
import { useGetHousesQuery } from '../../slices/housesApiSlice';
import { useGetTenantsQuery } from '../../slices/tenantsApiSlice';
import { useGetLandlordsQuery } from '../../slices/landlordsApiSlice';

const AddRentForm = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    house: '',
    tenant: '',
    landlord: '',
    startDate: '',
    endDate: '',
    lastRentEnd: '',
    monthlyRent: '',
    numberOfMonths: 1,
    totalRent: '',
    address: '',
    date: '',
    datePaid: '',
    remark: '',
    images: [],
  });

  const [tenantFilter, setTenantFilter] = useState('');
  const [createRent, { isLoading, error }] = useCreateRentMutation();
  const { data: housesData } = useGetHousesQuery({ keyword: '' });
  const { data: tenantsData } = useGetTenantsQuery({ keyword: '' });
  const { data: landlordsData } = useGetLandlordsQuery({ keyword: '' });

  const houses = housesData?.houses || [];
  const tenants = tenantsData?.tenants || [];
  const landlords = landlordsData?.landlords || [];

  // Default date to today
  useEffect(() => {
    if (!formData.date) {
      const today = new Date().toISOString().split('T')[0];
      setFormData((prev) => ({ ...prev, date: today }));
    }
  }, [formData.date]);

  // Compute total rent whenever monthlyRent or numberOfMonths changes
  useEffect(() => {
    const { monthlyRent, numberOfMonths } = formData;
    if (monthlyRent && numberOfMonths) {
      setFormData((prev) => ({
        ...prev,
        totalRent: monthlyRent * numberOfMonths,
      }));
    }
  }, [formData.monthlyRent, formData.numberOfMonths]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "tenant") {
      const selectedTenant = tenants.find((t) => t._id === value);
      setFormData((prev) => ({
        ...prev,
        tenant: value,
        house: selectedTenant?.house?._id || selectedTenant?.house || "",
        landlord: selectedTenant?.landlord?._id || selectedTenant?.landlord || "",
        monthlyRent: selectedTenant?.monthlyRent || "",
        address: selectedTenant?.address || "",
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: ["monthlyRent", "numberOfMonths"].includes(name) ? Number(value) : value,
      }));
    }
  };

  const validateFiles = (files) => {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    const maxSize = 5 * 1024 * 1024;
    for (let file of files) {
      if (!allowedTypes.includes(file.type)) {
        toast.error(`Unsupported file type: ${file.name}`);
        return false;
      }
      if (file.size > maxSize) {
        toast.error(`File too large: ${file.name}`);
        return false;
      }
    }
    return true;
  };

  const handleFileUpload = async (files) => {
    if (!validateFiles(files)) return;
    const uploadData = new FormData();
    Array.from(files).forEach((file) => uploadData.append('images', file));
    try {
      const res = await fetch('/api/upload', { method: 'POST', body: uploadData });
      const data = await res.json();
      if (res.ok) {
        setFormData((prev) => ({
          ...prev,
          images: [...prev.images, ...data.images.map((img) => img.path || img)].filter(Boolean),
        }));
        toast.success('Images uploaded');
      } else {
        throw new Error(data.message || 'Upload failed');
      }
    } catch (err) {
      toast.error(err.message);
    }
  };

  const removeImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    const { totalRent } = formData;

    if (!totalRent || totalRent <= 0) {
      toast.error('Total rent must be greater than 0');
      return;
    }

    try {
      await createRent(formData).unwrap();
      toast.success('Rent record added');
      navigate('/admin/rentlistscreen');
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to add rent');
    }
  };

  const filteredTenants = tenants.filter((t) =>
    t.name.toLowerCase().includes(tenantFilter.toLowerCase()) ||
    t.address?.toLowerCase().includes(tenantFilter.toLowerCase())
  );

    return (
    <div className="container mt-4">
      <h1>Add New Tenant Rent Record</h1>
      <Form onSubmit={submitHandler}>
        <Form.Group className="mb-3">
          <Form.Label>Filter Tenants</Form.Label>
          <Form.Control
            type="text"
            placeholder="Search tenants..."
            value={tenantFilter}
            onChange={(e) => setTenantFilter(e.target.value)}
          />
        </Form.Group>

        <Form.Group className="mb-3" controlId="tenant">
          <Form.Label>Tenant</Form.Label>
          <Form.Select name="tenant" value={formData.tenant} onChange={handleChange} required>
            <option value="">Select Tenant</option>
            {filteredTenants.map((t) => (
              <option key={t._id} value={t._id}>
                {t.name} — {t.address}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        {/* House: show read-only if assigned, else dropdown */}
        <Form.Group className="mb-3" controlId="house">
          <Form.Label>House</Form.Label>
          {formData.house ? (
            <Form.Control
              type="text"
              value={houses.find((h) => h._id === formData.house)?.name || ""}
              disabled
            />
          ) : (
            <Form.Select
              name="house"
              value={formData.house}
              onChange={handleChange}
              required
            >
              <option value="">Select House</option>
              {houses.map((h) => (
                <option key={h._id} value={h._id}>
                  {h.name} — {h.address}
                </option>
              ))}
            </Form.Select>
          )}
        </Form.Group>

        {/* Landlord: show read-only if assigned, else dropdown */}
        <Form.Group className="mb-3" controlId="landlord">
          <Form.Label>Landlord</Form.Label>
          {formData.landlord ? (
            <Form.Control
              type="text"
              value={landlords.find((l) => l._id === formData.landlord)?.name || ""}
              disabled
            />
          ) : (
            <Form.Select
              name="landlord"
              value={formData.landlord}
              onChange={handleChange}
              required
            >
              <option value="">Select Landlord</option>
              {landlords.map((l) => (
                <option key={l._id} value={l._id}>
                  {l.name}
                </option>
              ))}
            </Form.Select>
          )}
        </Form.Group>

        {/* Dates */}
        <Form.Group className="mb-3" controlId="startDate">
          <Form.Label>Start Date</Form.Label>
          <Form.Control
            type="date"
            name="startDate"
            value={formData.startDate}
            onChange={handleChange}
            required
          />
        </Form.Group>

        <Form.Group className="mb-3" controlId="endDate">
          <Form.Label>End Date</Form.Label>
          <Form.Control
            type="date"
            name="endDate"
            value={formData.endDate}
            onChange={handleChange}
            required
          />
        </Form.Group>

        <Form.Group className="mb-3" controlId="lastRentEnd">
          <Form.Label>Last Rent End</Form.Label>
          <Form.Control
            type="date"
            name="lastRentEnd"
            value={formData.lastRentEnd}
            onChange={handleChange}
          />
        </Form.Group>

        {/* Rent details */}
        <Form.Group className="mb-3" controlId="monthlyRent">
          <Form.Label>Monthly Rent</Form.Label>
          <Form.Control
            type="number"
            name="monthlyRent"
            value={formData.monthlyRent}
            onChange={handleChange}
            required
          />
        </Form.Group>

        <Form.Group className="mb-3" controlId="numberOfMonths">
          <Form.Label>Number of Months</Form.Label>
          <Form.Control
            type="number"
            name="numberOfMonths"
            value={formData.numberOfMonths}
            onChange={handleChange}
            required
          />
        </Form.Group>

        <Form.Group className="mb-3" controlId="totalRent">
          <Form.Label>Total Rent</Form.Label>
          <Form.Control
            type="number"
            name="totalRent"
            value={formData.totalRent}
            readOnly
          />
        </Form.Group>

        <Form.Group className="mb-3" controlId="address">
          <Form.Label>Tenant Address</Form.Label>
          <Form.Control
            type="text"
            name="address"
            value={formData.address}
            onChange={handleChange}
            required
          />
        </Form.Group>

        <Form.Group className="mb-3" controlId="date">
          <Form.Label>Recorded At</Form.Label>
          <Form.Control
            type="date"
            name="date"
            value={formData.date}
            onChange={handleChange}
            required
          />
        </Form.Group>

        <Form.Group className="mb-3" controlId="datePaid">
          <Form.Label>Date Paid</Form.Label>
          <Form.Control
            type="date"
            name="datePaid"
            value={formData.datePaid}
            onChange={handleChange}
          />
        </Form.Group>

        <Form.Group className="mb-3" controlId="remark">
          <Form.Label>Remark</Form.Label>
          <Form.Control
            as="textarea"
            name="remark"
            value={formData.remark}
            onChange={handleChange}
            rows={3}
          />
        </Form.Group>

        {/* Image upload */}
        <Form.Group className="mb-3" controlId="images">
          <Form.Label>Upload Rent Images</Form.Label>
          <Form.Control
            type="file"
            multiple
            accept="image/*"
            onChange={(e) => handleFileUpload(e.target.files)}
          />
          <div className="mt-3 d-flex flex-wrap gap-2">
            {formData.images.map((img, idx) => (
              <div
                key={idx}
                style={{
                  position: 'relative',
                  width: '100px',
                  height: '100px',
                  marginRight: '10px',
                }}
              >
                <img
                  src={img}
                  alt={`Uploaded ${idx}`}
                  className="img-fluid"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: '4px',
                  }}
                />
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => removeImage(idx)}
                  style={{
                    position: 'absolute',
                    top: '0',
                    right: '0',
                    borderRadius: '50%',
                    padding: '2px 6px',
                    lineHeight: '1',
                  }}
                >
                  ×
                </Button>
              </div>
            ))}
          </div>
        </Form.Group>

        {/* Submit button */}
        <Button type="submit" variant="primary" disabled={isLoading}>
          {isLoading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Adding Rent...
            </>
          ) : (
            'Add Rent'
          )}
        </Button>

        {/* Error alert */}
        {error && (
          <Alert variant="danger" className="mt-3">
            {error?.data?.message || 'Error adding rent record'}
          </Alert>
        )}
      </Form>
    </div>
  );
};

export default AddRentForm;